import React, { useEffect, useState } from "react";
import { Switch, Route, Router as WouterRouter, useLocation, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider, SignIn, useUser, useClerk } from "@clerk/react";
import { dark } from "@clerk/themes";
import { publishableKeyFromHost } from "@clerk/react/internal";

import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useGetMe } from "@workspace/api-client-react";
import { PullerFilterProvider } from "@/contexts/puller-filter";

import Home from "@/pages/home";
import Accounts from "@/pages/accounts";
import AccountDetail from "@/pages/account-detail";
import Banking from "@/pages/banking";
import Pricing from "@/pages/pricing";
import Emails from "@/pages/emails";
import Sales from "@/pages/sales";
import Activity from "@/pages/activity";
import SkyboxPage from "@/pages/skybox";
import Ticketransfer from "@/pages/ticketransfer";
import Settings from "@/pages/settings";
import Notifications from "@/pages/notifications";
import NotFound from "@/pages/not-found";
import { Loader2 } from "lucide-react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Without this, React Query's default staleTime of 0 means every
      // component mount triggers a refetch. Hooks like useGetMe() are rendered
      // per-row in the virtualized inventory table, so constant row mount/unmount
      // during scrolling and dropdown interactions floods /api/me with requests
      // (and each response re-renders the row, causing a visible flash).
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const clerkPubKey = publishableKeyFromHost(window.location.hostname, import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useUser();
  const [location] = useLocation();

  if (!isLoaded) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSignedIn && !location.startsWith("/sign-")) {
    return <Redirect to="/sign-in" />;
  }

  if (isSignedIn && location.startsWith("/sign-")) {
    return <Redirect to="/" />;
  }

  if (isSignedIn) {
    return <RoleGate>{children}</RoleGate>;
  }

  return <>{children}</>;
}

function RoleGate({ children }: { children: React.ReactNode }) {
  const { data: me, isLoading } = useGetMe();

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (me && !me.allowed) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background p-4">
        <div className="max-w-md w-full p-8 border border-border bg-card rounded-lg shadow-xl text-center space-y-4">
          <img src="/logo.svg" alt="TEX FVE Handler" className="w-12 h-12 mx-auto" />
          <h1 className="text-xl font-medium text-foreground">Access Restricted</h1>
          <p className="text-sm text-secondary-foreground">
            Your account ({me.email}) does not have permission to access the TEX FVE Handler.
          </p>
          <button 
            className="w-full h-8 bg-muted hover:bg-secondary text-foreground text-sm font-medium rounded-md border border-border"
            onClick={() => window.location.href = "/sign-in"}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return <PullerFilterProvider>{children}</PullerFilterProvider>;
}

function Router() {
  return (
    <Switch>
      <Route path="/sign-in/*?">
        <div className="min-h-screen w-full flex items-center justify-center bg-background">
          <SignIn routing="path" path={`${basePath}/sign-in`} />
        </div>
      </Route>
      <Route path="/sign-up/*?">
        <Redirect to="/sign-in" />
      </Route>
      <Route path="/">
        <AuthGate>
          <Home />
        </AuthGate>
      </Route>
      <Route path="/accounts">
        <AuthGate>
          <Accounts />
        </AuthGate>
      </Route>
      <Route path="/accounts/:id">
        <AuthGate>
          <AccountDetail />
        </AuthGate>
      </Route>
      <Route path="/banking">
        <AuthGate>
          <Banking />
        </AuthGate>
      </Route>
      <Route path="/pricing">
        <AuthGate>
          <Pricing />
        </AuthGate>
      </Route>
      <Route path="/emails">
        <AuthGate>
          <Emails />
        </AuthGate>
      </Route>
      <Route path="/sales">
        <AuthGate>
          <Sales />
        </AuthGate>
      </Route>
      <Route path="/activity">
        <AuthGate>
          <Activity />
        </AuthGate>
      </Route>
      <Route path="/skybox">
        <AuthGate>
          <SkyboxPage />
        </AuthGate>
      </Route>
      <Route path="/ticketransfer">
        <AuthGate>
          <Ticketransfer />
        </AuthGate>
      </Route>
      <Route path="/settings">
        <AuthGate>
          <Settings />
        </AuthGate>
      </Route>
      <Route path="/notifications">
        <AuthGate>
          <Notifications />
        </AuthGate>
      </Route>
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
}

function App() {
  const routerPush = (to: string) => window.history.pushState(null, "", to);
  const routerReplace = (to: string) => window.history.replaceState(null, "", to);

  return (
    <QueryClientProvider client={queryClient}>
      <WouterRouter base={basePath}>
        <ClerkProvider
          publishableKey={clerkPubKey}
          proxyUrl={clerkProxyUrl}
          signInUrl={`${basePath}/sign-in`}
          routerPush={routerPush}
          routerReplace={routerReplace}
          appearance={{
            baseTheme: dark,
            variables: {
              colorPrimary: "#887BE0",
              colorBackground: "#0A0A0B",
              colorInput: "#0E0E10",
              colorForeground: "#ECECEE",
              colorMutedForeground: "#B4B4BB",
              fontFamily: "'Inter', sans-serif",
              borderRadius: "6px"
            },
            elements: {
              card: "bg-card border border-border shadow-xl",
              headerTitle: "text-foreground",
              headerSubtitle: "text-secondary-foreground",
              socialButtonsBlockButton: "!bg-white !text-gray-900 !border !border-gray-200 hover:!bg-gray-50",
              socialButtonsBlockButtonText: "!text-gray-900",
              socialButtonsBlockButtonArrow: "!text-gray-900",
              form: "!hidden",
              formFieldRow: "!hidden",
              formButtonPrimary: "!hidden",
              dividerRow: "!hidden",
              dividerLine: "!hidden",
              dividerText: "!hidden",
              footerAction: "!hidden"
            }
          }}
        >
          <TooltipProvider>
            <Router />
            <Toaster />
          </TooltipProvider>
        </ClerkProvider>
      </WouterRouter>
    </QueryClientProvider>
  );
}

export default App;
